import { QueryClient, useQuery } from '@tanstack/react-query';
import { SessionExportRowDto, MemberTrendsRowDto } from '@repo/shared/dtos/analytics/ExportDto';
import { ScopeUnitDto } from '@repo/shared/dtos/analytics/ScopeFilterDto';
import { CACHE_TIMES, DEFAULT_QUERY_OPTIONS } from './config';
import { analyticsQueryKeys } from './queryKeys';
import { fetchAnalyticsData, serializeFilters } from './utils';

const EXPORT_BASE_URL = '/api/analytics/admin/export';

export interface ExportReportParams {
  /** YYYY-MM-DD */
  startDate: string;
  /** YYYY-MM-DD */
  endDate: string;
  unitIds: number[];
}

// Units in the caller's export scope. Only needed for roles without the scope filter
// (UnitAdmin), which can't load the filter bar's unit list.
export const useExportScopeUnits = (enabled: boolean) =>
  useQuery({
    queryKey: analyticsQueryKeys.export.units(),
    queryFn: ({ signal }) =>
      fetchAnalyticsData<ScopeUnitDto[]>(`${EXPORT_BASE_URL}/options/units`, signal),
    enabled,
    staleTime: CACHE_TIMES.STALE_TIME,
    gcTime: CACHE_TIMES.GC_TIME,
    ...DEFAULT_QUERY_OPTIONS,
  });

const fetchReport = <T>(queryClient: QueryClient, endpoint: string, params: ExportReportParams) => {
  const search = serializeFilters({ ...params }).toString();
  return queryClient.fetchQuery({
    queryKey: analyticsQueryKeys.export.report(endpoint, search),
    queryFn: ({ signal }) =>
      fetchAnalyticsData<T>(`${EXPORT_BASE_URL}${endpoint}?${search}`, signal),
    staleTime: CACHE_TIMES.STALE_TIME,
  });
};

export const fetchSessionExport = (queryClient: QueryClient, params: ExportReportParams) =>
  fetchReport<SessionExportRowDto[]>(queryClient, '/sessions', params);

export const fetchMemberTrendsExport = (queryClient: QueryClient, params: ExportReportParams) =>
  fetchReport<MemberTrendsRowDto[]>(queryClient, '/member-trends', params);
